/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Injectable
} from '@angular/core';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	JsonSchemaLayoutHelper
} from '@shared/helpers/json-schema-layout.helper';

import {
	IFieldProperties
} from '@shared/interfaces/application-objects/field-properties.interface';
import * as jref from 'jref';

/**
 * A class for generating Formly layouts from JSON Schema definitions.
 *
 * @export
 * @class JsonSchemaLayoutService
 */
@Injectable({
	providedIn: 'root'
})
export class JsonSchemaLayoutService
{
	/**
	 * JSON Schema type constants.
	 *
	 * @private
	 * @readonly
	 * @type {Record<string, string>}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly schemaTypes: Record<string, string> =
		{
			array: AppConstants.propertyTypes.array,
			boolean: AppConstants.propertyTypes.boolean,
			integer: AppConstants.propertyTypes.integer,
			number: AppConstants.propertyTypes.number,
			object: AppConstants.propertyTypes.object,
			string: AppConstants.propertyTypes.string
		};

	/**
	 * JSON Schema format constants.
	 *
	 * @private
	 * @readonly
	 * @type {Record<string, string>}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly schemaFormats: Record<string, string> =
		{
			date: AppConstants.jsonSchemaFormats.date,
			dateTime: AppConstants.jsonSchemaFormats.dateTime
		};

	/**
	 * JSON Schema property name constants.
	 *
	 * @private
	 * @readonly
	 * @type {Record<string, string>}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly schemaProperties: Record<string, string> =
		{
			anyOf: AppConstants.jsonSchemaProperties.anyOf,
			const: AppConstants.jsonSchemaProperties.const,
			data: AppConstants.jsonSchemaProperties.data,
			default: AppConstants.jsonSchemaProperties.default,
			enum: AppConstants.jsonSchemaProperties.enum,
			format: AppConstants.jsonSchemaProperties.format,
			items: AppConstants.jsonSchemaProperties.items,
			properties: AppConstants.jsonSchemaProperties.properties,
			required: AppConstants.jsonSchemaProperties.required,
			resourceIdentifier:
				AppConstants.commonProperties.resourceIdentifier,
			type: AppConstants.commonProperties.type
		};

	/**
	 * Prefix for data property paths (e.g., 'data.fieldName').
	 *
	 * @private
	 * @readonly
	 * @type {string}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly dataPropertyPrefix: string =
		AppConstants.nestedDataIdentifier;

	/**
	 * Default empty string value for filter properties.
	 *
	 * @private
	 * @readonly
	 * @type {string}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly filterDefaultValue: string = AppConstants.empty;

	/**
	 * Default placeholder text for select controls.
	 *
	 * @private
	 * @readonly
	 * @type {string}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly defaultSelectPlaceholder: string = 'Select an Option';

	/**
	 * Threshold for creating tabs vs sections.
	 * Objects with more than this many properties become tabs.
	 *
	 * @private
	 * @readonly
	 * @type {number}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly tabThreshold: number = 9;

	/**
	 * Maximum properties to include in repeater titleFormat.
	 *
	 * @private
	 * @readonly
	 * @type {number}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly maxTitleFormatProperties: number = 2;

	/**
	 * Property names that should use custom-image-input control.
	 *
	 * @private
	 * @readonly
	 * @type {string[]}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly imageTypes: string[] = ['image', 'logo'];

	/**
	 * Property types that should use custom-input-number control.
	 *
	 * @private
	 * @readonly
	 * @type {string[]}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly inputNumberTypes: string[] =
		[
			AppConstants.propertyTypes.number,
			AppConstants.propertyTypes.integer
		];

	/**
	 * Controls whether anyOf repeater fields should be generated.
	 * When false, repeaters with anyOf schemas will be skipped.
	 * Regular repeaters without anyOf will continue to work normally.
	 *
	 * @private
	 * @readonly
	 * @type {boolean}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly enableAnyOfRepeaters: boolean = true;

	/**
	 * Layout field type sort order configuration.
	 * Defines the order in which different field types should appear.
	 * Lower numbers appear first. Adjust these values to change the sort order.
	 *
	 * Default order:
	 * 1. Fields (inputs, selects, etc.)
	 * 2. Sections (objects with section titles)
	 * 3. Repeaters (arrays)
	 *
	 * @private
	 * @readonly
	 * @type {Record<string, number>}
	 * @memberof JsonSchemaLayoutService
	 */
	private readonly layoutFieldSortOrder: Record<string, number> =
		{
			field: 1, // Regular input fields, selects, checkboxes, etc.
			section: 2, // Section titles and objects
			repeater: 3 // Array repeaters
		};

	/**
	 * Generates a Formly layout from an entity type definition.
	 *
	 * This method takes a JSON Schema (EntityTypeDefinition) and converts it
	 * into a Formly layout array suitable for rendering dynamic forms.
	 * Arrays become repeaters with NO [0] indices, complex objects become
	 * tabs, and simple objects are flattened.
	 *
	 * @example
	 * const schema = {
	 *   properties: {
	 *     data: {
	 *       type: 'object',
	 *       properties: {
	 *         name: { type: 'string' },
	 *         age: { type: 'number' }
	 *       }
	 *     }
	 *   }
	 * };
	 * const layout = service.generateLayout(schema, false);
	 *
	 * @param {*} entityDefinition
	 * The entity type definition containing the JSON schema.
	 * @param {boolean} [isDisabled=false]
	 * Whether fields should be disabled by default.
	 * @returns {FormlyFieldConfig[]}
	 * Array of layout field configurations.
	 * @memberof JsonSchemaLayoutService
	 */
	public generateLayout(
		entityDefinition: any,
		isDisabled: boolean = false): FormlyFieldConfig[]
	{
		const dereferencedSchema: any =
			this.dereferenceSchema(entityDefinition);

		const schemaProperties: any =
			dereferencedSchema?.[this.schemaProperties.properties];
		const dataObject = schemaProperties?.[this.schemaProperties.data];
		const dataProperties: any =
			dataObject?.[this.schemaProperties.properties];
		const requiredFields: string[] =
			dataObject?.[this.schemaProperties.required] ?? [];

		if (AnyHelper.isNull(dataProperties))
		{
			return [];
		}

		const fields: FormlyFieldConfig[] =
			this.traverseDataProperties(
				dataProperties,
				requiredFields,
				isDisabled);

		return this.wrapWithSummaryTabIfNeeded(fields);
	}

	/**
	 * Determines the sort order value for a field based on its type.
	 *
	 * @private
	 * @param {FormlyFieldConfig} field
	 * The field to evaluate.
	 * @returns {number}
	 * The sort order value.
	 * @memberof JsonSchemaLayoutService
	 */
	private getFieldSortOrder(field: FormlyFieldConfig): number
	{
		if (field.type === FormlyConstants.customControls.customRepeater)
		{
			return this.layoutFieldSortOrder.repeater;
		}

		if (field.type === FormlyConstants.customControls.customSectionTitle)
		{
			return this.layoutFieldSortOrder.section;
		}

		if (field.wrappers?.includes(
			FormlyConstants.customControls.customTabContent))
		{
			return this.layoutFieldSortOrder.section;
		}

		return this.layoutFieldSortOrder.field;
	}

	/**
	 * Sorts field groups by their field type order
	 * (fields, sections, repeaters).
	 * Empty groups are sorted to the end.
	 *
	 * @private
	 * @param {FormlyFieldConfig[][]} fieldGroups
	 * Array of field groups to sort.
	 * @returns {FormlyFieldConfig[][]}
	 * Sorted field groups.
	 * @memberof JsonSchemaLayoutService
	 */
	private sortFieldGroups(
		fieldGroups: FormlyFieldConfig[][]): FormlyFieldConfig[][]
	{
		return fieldGroups.sort(
			(itemOne: FormlyFieldConfig[], itemTwo: FormlyFieldConfig[]) =>
			{
				if (itemOne.length === 0 && itemTwo.length === 0)
				{
					return 0;
				}

				if (itemOne.length === 0)
				{
					return 1;
				}

				if (itemTwo.length === 0)
				{
					return -1;
				}

				const orderOne: number = this.getFieldSortOrder(itemOne[0]);
				const orderTwo: number = this.getFieldSortOrder(itemTwo[0]);

				return orderOne - orderTwo;
			});
	}

	/**
	 * Flattens an array of field groups into a single flat array of fields.
	 *
	 * @private
	 * @param {FormlyFieldConfig[][]} fieldGroups
	 * Array of field groups to flatten.
	 * @returns {FormlyFieldConfig[]}
	 * Flattened array of fields.
	 * @memberof JsonSchemaLayoutService
	 */
	private flattenFieldGroups(
		fieldGroups: FormlyFieldConfig[][]): FormlyFieldConfig[]
	{
		const fields: FormlyFieldConfig[] = [];
		for (const group of fieldGroups)
		{
			fields.push(...group);
		}

		return fields;
	}

	/**
	 * Builds field groups from properties, sorts them by type order
	 * (fields, sections, repeaters), and returns a flattened array.
	 *
	 * @private
	 * @param {any} properties
	 * The properties object to process.
	 * @param {string[]} requiredFields
	 * Array of required field names.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @param {boolean} insideTab
	 * Whether fields are inside a tab.
	 * @param {string} keyPrefix
	 * Prefix for field keys (e.g., 'data.' or nested path).
	 * @returns {FormlyFieldConfig[]}
	 * Flattened and sorted array of field configurations.
	 * @memberof JsonSchemaLayoutService
	 */
	private buildFieldGroups(
		properties: any,
		requiredFields: string[],
		isDisabled: boolean,
		insideTab: boolean,
		keyPrefix: string): FormlyFieldConfig[]
	{
		const fieldGroups: FormlyFieldConfig[][] = [];

		for (const propertyName of Object.keys(properties))
		{
			const property: any = properties[propertyName];
			const key: string = this.buildPropertyKey(keyPrefix, propertyName);
			const isRequired: boolean = requiredFields.includes(propertyName);

			const fieldGroup: FormlyFieldConfig[] =
				this.createFieldFromProperty(
					key,
					propertyName,
					property,
					isRequired,
					isDisabled,
					insideTab);

			fieldGroups.push(fieldGroup);
		}

		// Sort field groups: regular fields, sections, repeaters
		const sortedFieldGroups: FormlyFieldConfig[][] =
			this.sortFieldGroups(fieldGroups);

		// Flatten sorted groups
		return this.flattenFieldGroups(sortedFieldGroups);
	}

	/**
	 * Builds a property key path by combining a prefix with a property name.
	 * Handles cases where the prefix already ends with a dot separator.
	 *
	 * @private
	 * @param {string} keyPrefix
	 * The prefix for the key (may be empty or end with '.').
	 * @param {string} propertyName
	 * The property name to append.
	 * @returns {string}
	 * The combined property key path.
	 * @memberof JsonSchemaLayoutService
	 */
	private buildPropertyKey(
		keyPrefix: string,
		propertyName: string): string
	{
		if (!keyPrefix)
		{
			return propertyName;
		}

		if (keyPrefix.endsWith('.'))
		{
			return `${keyPrefix}${propertyName}`;
		}

		return `${keyPrefix}.${propertyName}`;
	}

	/**
	 * Dereferences a JSON schema by resolving all $ref references.
	 *
	 * @private
	 * @param {*} schema
	 * The schema to dereference.
	 * @returns {*}
	 * The dereferenced schema.
	 * @memberof JsonSchemaLayoutService
	 */
	private dereferenceSchema(schema: any): any
	{
		return jref.dereference(schema);
	}

	/**
	 * Traverses data properties and generates layout fields.
	 *
	 * @private
	 * @param {*} dataProperties
	 * The properties object from the schema.
	 * @param {string[]} requiredFields
	 * Array of required field names.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig[]}
	 * Array of generated layout fields.
	 * @memberof JsonSchemaLayoutService
	 */
	private traverseDataProperties(
		dataProperties: any,
		requiredFields: string[],
		isDisabled: boolean): FormlyFieldConfig[]
	{
		return this.buildFieldGroups(
			dataProperties,
			requiredFields,
			isDisabled,
			false,
			this.dataPropertyPrefix);
	}

	/**
	 * Creates a layout field from a property definition.
	 *
	 * @private
	 * @param {string} key
	 * The full key path for the field.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isRequired
	 * Whether the field is required.
	 * @param {boolean} isDisabled
	 * Whether the field is disabled.
	 * @param {boolean} [insideTab=false]
	 * Whether this field is inside a tab (affects object flattening).
	 * @returns {FormlyFieldConfig[]}
	 * Array of layout fields (may include tabs or sections).
	 * @memberof JsonSchemaLayoutService
	 */
	private createFieldFromProperty(
		key: string,
		propertyName: string,
		property: any,
		isRequired: boolean,
		isDisabled: boolean,
		insideTab: boolean = false): FormlyFieldConfig[]
	{
		const propertyType: string = this.getPropertyType(property);

		if (propertyType === this.schemaTypes.array)
		{
			return this.handleArrayProperty(
				key,
				propertyName,
				property,
				isRequired,
				isDisabled);
		}

		if (propertyType === this.schemaTypes.object)
		{
			return this.handleObjectProperty(
				key,
				propertyName,
				property,
				isDisabled,
				insideTab);
		}

		return [
			this.createField(
				key,
				propertyName,
				property,
				isRequired,
				isDisabled)];
	}

	/**
	 * Handles array property field generation.
	 * Dispatches to appropriate handler based on array item type.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isRequired
	 * Whether the field is required.
	 * @param {boolean} isDisabled
	 * Whether the field is disabled.
	 * @returns {FormlyFieldConfig[]}
	 * The generated field configuration array.
	 * @memberof JsonSchemaLayoutService
	 */
	private handleArrayProperty(
		key: string,
		propertyName: string,
		property: any,
		isRequired: boolean,
		isDisabled: boolean): FormlyFieldConfig[]
	{
		const arrayItemsSchema: any = this.getSchemaItems(property);

		// Skip arrays without items definition (invalid/incomplete schema)
		if (AnyHelper.isNull(arrayItemsSchema))
		{
			return [];
		}

		const itemType: string = this.getPropertyType(arrayItemsSchema);

		// Check for multiselects (enum arrays of simple types)
		if (itemType !== this.schemaTypes.object
			&& this.getSchemaEnum(arrayItemsSchema))
		{
			return [
				this.createField(
					key,
					propertyName,
					property,
					isRequired,
					isDisabled)];
		}

		// Check for anyOf repeaters
		const anyOfArray: any = this.getAnyOfArray(arrayItemsSchema);
		const hasAnyOf: boolean =
			!AnyHelper.isNull(anyOfArray) && Array.isArray(anyOfArray);

		if (hasAnyOf
			&& this.enableAnyOfRepeaters
			&& this.isValidSingleTypeAnyOf(anyOfArray))
		{
			return this.handleArrayWithAnyOf(
				key,
				propertyName,
				arrayItemsSchema,
				isDisabled);
		}

		// Regular object repeater
		return this.createRegularArrayRepeater(
			key,
			propertyName,
			property,
			isDisabled);
	}

	/**
	 * Handles object property field generation.
	 * Dispatches to appropriate handler based on context (tab vs section).
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isDisabled
	 * Whether the field is disabled.
	 * @param {boolean} insideTab
	 * Whether currently inside a tab (prevents nested tabs).
	 * @returns {FormlyFieldConfig[]}
	 * The generated field configuration array.
	 * @memberof JsonSchemaLayoutService
	 */
	private handleObjectProperty(
		key: string,
		propertyName: string,
		property: any,
		isDisabled: boolean,
		insideTab: boolean): FormlyFieldConfig[]
	{
		// If inside tab, create section (never nested tabs)
		if (insideTab)
		{
			return this.createSection(
				key,
				propertyName,
				property,
				isDisabled,
				insideTab);
		}

		if (this.shouldUseTab(property))
		{
			return [
				this.createTab(
					key,
					propertyName,
					property,
					isDisabled)];
		}

		// All non-tab objects become sections (section title + flat fields)
		return this.createSection(
			key,
			propertyName,
			property,
			isDisabled,
			insideTab);
	}

	/**
	 * Handles arrays with anyOf schemas (discriminated union repeaters).
	 * Creates multiple repeaters and optionally wraps them in a tab.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} arrayItemsSchema
	 * The array items schema definition.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig[]}
	 * The generated anyOf repeater configurations.
	 * @memberof JsonSchemaLayoutService
	 */
	private handleArrayWithAnyOf(
		key: string,
		propertyName: string,
		arrayItemsSchema: any,
		isDisabled: boolean): FormlyFieldConfig[]
	{
		const anyOfRepeaters: FormlyFieldConfig[] =
			this.createAnyOfRepeaters(
				key,
				propertyName,
				arrayItemsSchema,
				isDisabled);

		if (this.shouldTabifyAnyOfRepeaters(key))
		{
			return [
				this.wrapAnyOfRepeatersInTab(
					propertyName,
					anyOfRepeaters)];
		}

		return anyOfRepeaters;
	}

	/**
	 * Creates a standard repeater for array properties.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig[]}
	 * The generated repeater configuration.
	 * @memberof JsonSchemaLayoutService
	 */
	private createRegularArrayRepeater(
		key: string,
		propertyName: string,
		property: any,
		isDisabled: boolean): FormlyFieldConfig[]
	{
		return [
			this.createRepeater(
				key,
				propertyName,
				property,
				isDisabled)];
	}

	/**
	 * Creates a basic field layout.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isRequired
	 * Whether the field is required.
	 * @param {boolean} isDisabled
	 * Whether the field is disabled.
	 * @returns {FormlyFieldConfig}
	 * The generated field configuration.
	 * @memberof JsonSchemaLayoutService
	 */
	private createField(
		key: string,
		propertyName: string,
		property: any,
		isRequired: boolean,
		isDisabled: boolean): FormlyFieldConfig
	{
		const controlType: string =
			JsonSchemaLayoutHelper.inferControlType(
				property,
				propertyName,
				this.schemaTypes,
				this.schemaFormats,
				this.schemaProperties,
				this.imageTypes,
				this.inputNumberTypes);

		// Extract enum from property or items (for multi-select arrays)
		let enumValues: any[] = this.getSchemaEnum(property);

		if (AnyHelper.isNull(enumValues))
		{
			const itemsSchema: any = this.getSchemaItems(property);

			if (!AnyHelper.isNull(itemsSchema))
			{
				enumValues = this.getSchemaEnum(itemsSchema);
			}
		}
		const constValue: any =
			property[this.schemaProperties.const];
		const defaultValue: any = property[this.schemaProperties.default];

		const fieldProps: IFieldProperties =
			JsonSchemaLayoutHelper.buildFieldProps(
				property,
				propertyName,
				controlType,
				{
					isRequired: isRequired,
					isDisabled: isDisabled,
					enumValues: enumValues,
					constValue: constValue,
					defaultValue: defaultValue,
					defaultSelectPlaceholder: this.defaultSelectPlaceholder
				});

		const wrapperNeeded: boolean =
			JsonSchemaLayoutHelper.shouldHaveWrapper(
				controlType,
				FormlyConstants.customControls);

		return JsonSchemaLayoutHelper.createField(
			key,
			controlType,
			fieldProps,
			wrapperNeeded);
	}

	/**
	 * Validates if an anyOf array contains exactly one type per variant.
	 *
	 * @private
	 * @param {any[]} anyOfSchemas
	 * Array of anyOf schema variants.
	 * @returns {boolean}
	 * True if valid (each variant has one type const).
	 * @memberof JsonSchemaLayoutService
	 */
	private isValidSingleTypeAnyOf(anyOfSchemas: any[]): boolean
	{
		if (!Array.isArray(anyOfSchemas) || anyOfSchemas.length === 0)
		{
			return false;
		}

		return anyOfSchemas.every(
			(schema: any) =>
			{
				const schemaProps: any =
					this.getSchemaProperties(schema);
				const typeProperty: any =
					schemaProps[this.schemaProperties.type];

				// Must have a type property with a const value
				const constValue: any =
					typeProperty?.[this.schemaProperties.const];

				return !AnyHelper.isNull(constValue)
					&& typeof constValue === AppConstants.propertyTypes.string;
			});
	}

	/**
	 * Creates multiple repeaters for anyOf discriminated unions.
	 * Each anyOf type gets its own repeater with a shared key and type filter.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name for the array.
	 * @param {*} itemSchema
	 * The items schema containing anyOf variants.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig[]}
	 * Array of repeater configurations, one per anyOf type.
	 * @memberof JsonSchemaLayoutService
	 */
	private createAnyOfRepeaters(
		key: string,
		propertyName: string,
		itemSchema: any,
		isDisabled: boolean): FormlyFieldConfig[]
	{
		const anyOfRepeaters: FormlyFieldConfig[] = [];
		const baseLabel: string =
			JsonSchemaLayoutHelper.generateLabel(propertyName);

		// Process each anyOf variant into a repeater
		for (const anyOfVariant of itemSchema.anyOf)
		{
			const repeater: FormlyFieldConfig | null =
				this.createSingleAnyOfRepeater(
					key,
					baseLabel,
					anyOfVariant,
					isDisabled);

			if (repeater !== null)
			{
				anyOfRepeaters.push(repeater);
			}
		}

		// Sort alphabetically by label
		return this.sortRepeatersByLabel(anyOfRepeaters);
	}

	/**
	 * Extracts the type const value from an anyOf variant.
	 *
	 * @private
	 * @param {*} variant
	 * The anyOf variant schema.
	 * @returns {(string | null)}
	 * The type const value or null if not found.
	 * @memberof JsonSchemaLayoutService
	 */
	private extractTypeConstFromVariant(variant: any): string | null
	{
		const variantProperties: any =
			this.getSchemaProperties(variant);
		const typeProperty: any =
			variantProperties[this.schemaProperties.type];

		if (AnyHelper.isNull(typeProperty))
		{
			return null;
		}

		const typeConst: any =
			typeProperty[this.schemaProperties.const];

		return typeConst;
	}

	/**
	 * Filters properties to remove arrays (prevents repeaters-in-repeaters).
	 *
	 * @private
	 * @param {*} properties
	 * The properties object to filter.
	 * @returns {*}
	 * The filtered properties object.
	 * @memberof JsonSchemaLayoutService
	 */
	private filterRepeaterProperties(properties: any): any
	{
		const filteredProperties: any = {};

		for (const propName of Object.keys(properties))
		{
			const prop: any = properties[propName];
			const propType: string = this.getPropertyType(prop ?? {});

			// Repeaters cannot be shown in repeaters
			if (propType === this.schemaTypes.array)
			{
				continue; // Skip array properties
			}

			filteredProperties[propName] = prop;
		}

		return filteredProperties;
	}

	/**
	 * Creates a single anyOf repeater from one variant.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} baseLabel
	 * The base label for the repeater.
	 * @param {*} variant
	 * The anyOf variant schema.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {(FormlyFieldConfig | null)}
	 * The repeater configuration or null if invalid.
	 * @memberof JsonSchemaLayoutService
	 */
	private createSingleAnyOfRepeater(
		key: string,
		baseLabel: string,
		variant: any,
		isDisabled: boolean): FormlyFieldConfig | null
	{
		// Extract and validate type const
		const typeConst: string | null =
			this.extractTypeConstFromVariant(variant);
		if (AnyHelper.isNull(typeConst))
		{
			return null; // Skip invalid variants
		}

		// Get and filter properties
		const variantProperties: any = this.getSchemaProperties(variant);
		const filteredProperties: any =
			this.filterRepeaterProperties(variantProperties);

		// Get required fields
		const variantRequired: string[] = this.getRequiredFields(variant);

		// Build field group
		const sortedFieldGroup: FormlyFieldConfig[] =
			this.buildFieldGroups(
				filteredProperties,
				variantRequired,
				isDisabled,
				true,
				AppConstants.empty);

		const typeLabel: string =
			JsonSchemaLayoutHelper.generateLabel(typeConst);
		const repeaterLabel: string =
			`${baseLabel} - ${typeLabel}`;
		const titleFormat: string | null =
			this.generateTitleFormat(filteredProperties);

		return JsonSchemaLayoutHelper.createAnyOfRepeater(
			key,
			repeaterLabel,
			typeConst,
			sortedFieldGroup,
			titleFormat);
	}

	/**
	 * Sorts repeaters alphabetically by label (case-insensitive).
	 *
	 * @private
	 * @param {FormlyFieldConfig[]} repeaters
	 * The repeaters to sort.
	 * @returns {FormlyFieldConfig[]}
	 * The sorted repeaters array.
	 * @memberof JsonSchemaLayoutService
	 */
	private sortRepeatersByLabel(
		repeaters: FormlyFieldConfig[]): FormlyFieldConfig[]
	{
		return repeaters.sort(
			(itemOne: FormlyFieldConfig,
				itemTwo: FormlyFieldConfig) =>
			{
				const labelOne: string =
					itemOne.props?.label ?? AppConstants.empty;
				const labelTwo: string =
					itemTwo.props?.label ?? AppConstants.empty;

				return labelOne.localeCompare(
					labelTwo,
					undefined,
					{
						sensitivity: 'base'
					});
			});
	}

	/**
	 * Creates a repeater field for arrays.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig}
	 * The generated repeater configuration.
	 * @memberof JsonSchemaLayoutService
	 */
	private createRepeater(
		key: string,
		propertyName: string,
		property: any,
		isDisabled: boolean): FormlyFieldConfig
	{
		const repeaterItemSchema: any = this.getSchemaItems(property);

		// Regular repeater logic
		const itemProperties: any =
			this.getSchemaProperties(repeaterItemSchema ?? {});

		const itemRequiredFields: string[] =
			this.getRequiredFields(repeaterItemSchema ?? {});

		const sortedFieldGroup: FormlyFieldConfig[] =
			this.buildFieldGroups(
				itemProperties,
				itemRequiredFields,
				isDisabled,
				true,
				AppConstants.empty);

		const titleFormat: string | null =
			this.generateTitleFormat(itemProperties);
		const label: string =
			JsonSchemaLayoutHelper.generateLabel(propertyName);
		const singular: string =
			JsonSchemaLayoutHelper.generateSingular(propertyName);

		return JsonSchemaLayoutHelper.createRepeater(
			key,
			label,
			singular,
			titleFormat,
			sortedFieldGroup,
			this.filterDefaultValue);
	}

	/**
	 * Creates a tab field for complex objects.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @returns {FormlyFieldConfig}
	 * The generated tab configuration.
	 * @memberof JsonSchemaLayoutService
	 */
	private createTab(
		key: string,
		propertyName: string,
		property: any,
		isDisabled: boolean): FormlyFieldConfig
	{
		const nestedProperties: any = this.getSchemaProperties(property);
		const nestedRequiredFields: string[] = this.getRequiredFields(property);

		const sortedFieldGroup: FormlyFieldConfig[] =
			this.buildFieldGroups(
				nestedProperties,
				nestedRequiredFields,
				isDisabled,
				true,
				key);

		const tabLabel: string =
			JsonSchemaLayoutHelper.generateLabel(propertyName);

		// Add section title as first element in tab
		const sectionTitle: FormlyFieldConfig =
			JsonSchemaLayoutHelper.createSectionTitle(tabLabel);

		const fieldGroupWithTitle: FormlyFieldConfig[] =
			[
				sectionTitle,
				...sortedFieldGroup
			];

		return JsonSchemaLayoutHelper.createTab(
			tabLabel,
			fieldGroupWithTitle);
	}

	/**
	 * Checks if the layout contains any tab fields.
	 *
	 * @private
	 * @param {FormlyFieldConfig[]} fields
	 * Array of layout fields to check.
	 * @returns {boolean}
	 * True if any field has the custom-tab-content wrapper.
	 * @memberof JsonSchemaLayoutService
	 */
	private hasTabFields(fields: FormlyFieldConfig[]): boolean
	{
		return fields.some(
			(field: FormlyFieldConfig) =>
				field.wrappers?.includes(
					FormlyConstants.customControls.customTabContent));
	}

	/**
	 * Wraps non-tab fields in a Summary tab if tabs exist in the layout.
	 * This ensures a consistent tabbed interface when any tabs are present.
	 *
	 * @private
	 * @param {FormlyFieldConfig[]} fields
	 * Array of layout fields.
	 * @returns {FormlyFieldConfig[]}
	 * Modified layout with Summary tab if needed.
	 * @memberof JsonSchemaLayoutService
	 */
	private wrapWithSummaryTabIfNeeded(
		fields: FormlyFieldConfig[]): FormlyFieldConfig[]
	{
		if (!this.hasTabFields(fields))
		{
			return fields;
		}

		const tabFields: FormlyFieldConfig[] = [];
		const nonTabFields: FormlyFieldConfig[] = [];

		// Separate tab fields from non-tab fields
		for (const field of fields)
		{
			if (field.wrappers?.includes(
				FormlyConstants.customControls.customTabContent))
			{
				tabFields.push(field);
			}
			else
			{
				nonTabFields.push(field);
			}
		}

		// If there are non-tab fields, wrap them in a Summary tab
		if (nonTabFields.length > 0)
		{
			const summaryTab: FormlyFieldConfig =
				JsonSchemaLayoutHelper.createSummaryTab(nonTabFields);

			// Return Summary tab first, then other tabs
			return [summaryTab, ...tabFields];
		}

		// If all fields are tabs, return as-is
		return fields;
	}

	/**
	 * Creates a section with nested fields for complex objects.
	 * Returns an array with a section title followed by flat fields.
	 *
	 * @private
	 * @param {string} key
	 * The field key path.
	 * @param {string} propertyName
	 * The property name.
	 * @param {*} property
	 * The property schema definition.
	 * @param {boolean} isDisabled
	 * Whether fields should be disabled.
	 * @param {boolean} insideTab
	 * Whether this section is inside a tab.
	 * @returns {FormlyFieldConfig[]}
	 * The generated section configuration (title + flat fields).
	 * @memberof JsonSchemaLayoutService
	 */
	private createSection(
		key: string,
		propertyName: string,
		property: any,
		isDisabled: boolean,
		insideTab: boolean): FormlyFieldConfig[]
	{
		const nestedProperties: any = this.getSchemaProperties(property);
		const nestedRequiredFields: string[] = this.getRequiredFields(property);
		const fields: FormlyFieldConfig[] = [];

		// Add section title
		const sectionTitle: FormlyFieldConfig =
			{
				type: FormlyConstants.customControls.customSectionTitle,
				props: {
					label: JsonSchemaLayoutHelper.generateLabel(propertyName)
				}
			};
		fields.push(sectionTitle);

		// Build and add nested field groups
		const sortedFieldGroup: FormlyFieldConfig[] =
			this.buildFieldGroups(
				nestedProperties,
				nestedRequiredFields,
				isDisabled,
				insideTab,
				key);

		fields.push(...sortedFieldGroup);

		return fields;
	}

	/**
	 * Generates a title format string for repeater items.
	 *
	 * @private
	 * @param {*} itemProperties
	 * The repeater item properties.
	 * @returns {string | undefined}
	 * The title format string, or undefined if no properties are available.
	 * @memberof JsonSchemaLayoutService
	 */
	private generateTitleFormat(itemProperties: any): string | null
	{
		const propertyKeys: string[] =
			Object.keys(itemProperties ?? {});

		if (propertyKeys.length === 0)
		{
			return null;
		}

		const displayKeys: string[] = propertyKeys
			.filter(
				(propertyKey: string) =>
				{
					const property: any =
						itemProperties[propertyKey];
					const propertyType: string =
						this.normalizeType(property?.type);

					// Exclude resource identifier, type, and logos/images.
					if (propertyKey === this.schemaProperties.resourceIdentifier
						|| propertyKey === this.schemaProperties.type
						|| this.imageTypes.includes(propertyKey.toLowerCase()))
					{
						return false;
					}

					// Only include primitive types (exclude arrays and objects)
					return propertyType !== this.schemaTypes.array
						&& propertyType !== this.schemaTypes.object;
				})
			.slice(0, this.maxTitleFormatProperties);

		if (displayKeys.length === 0)
		{
			return null;
		}

		return displayKeys
			.map((propertyKey: string) => `{${propertyKey}}`)
			.join(' - ');
	}

	/**
	 * Normalizes a type value that may be a string or array.
	 *
	 * @private
	 * @param {(string | string[])} type
	 * The type value.
	 * @returns {string}
	 * The normalized type string.
	 * @memberof JsonSchemaLayoutService
	 */
	private normalizeType(type: string | string[]): string
	{
		if (Array.isArray(type))
		{
			// Find first valid type (excluding 'null' and whitespace)
			const firstValidType: string | undefined =
				type.find(
					(typeName: string) =>
						typeName !== 'null' &&
							!AnyHelper.isNullOrWhitespace(typeName));

			return firstValidType ?? type[0] ?? this.schemaTypes.string;
		}

		return type ?? this.schemaTypes.string;
	}

	/**
	 * Gets the normalized type from a property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {string}
	 * The normalized type string.
	 * @memberof JsonSchemaLayoutService
	 */
	private getPropertyType(property: any): string
	{
		return this.normalizeType(
			property[this.schemaProperties.type]);
	}

	/**
	 * Gets the properties object from a property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {*}
	 * The properties object or empty object.
	 * @memberof JsonSchemaLayoutService
	 */
	private getSchemaProperties(property: any): any
	{
		return property[this.schemaProperties.properties] ?? {};
	}

	/**
	 * Gets the required fields array from a property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {string[]}
	 * The required fields array or empty array.
	 * @memberof JsonSchemaLayoutService
	 */
	private getRequiredFields(property: any): string[]
	{
		return property[this.schemaProperties.required] ?? [];
	}

	/**
	 * Gets the items schema from an array property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {*}
	 * The items schema or null.
	 * @memberof JsonSchemaLayoutService
	 */
	private getSchemaItems(property: any): any
	{
		return property[this.schemaProperties.items];
	}

	/**
	 * Gets the enum array from a property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {(any[] | null)}
	 * The enum array or null.
	 * @memberof JsonSchemaLayoutService
	 */
	private getSchemaEnum(property: any): any[] | null
	{
		if (AnyHelper.isNull(property))
		{
			return null;
		}

		return property[this.schemaProperties.enum];
	}

	/**
	 * Gets the anyOf array from a property schema.
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {(any[] | null)}
	 * The anyOf array or null.
	 * @memberof JsonSchemaLayoutService
	 */
	private getAnyOfArray(property: any): any[] | null
	{
		return property[this.schemaProperties.anyOf];
	}

	/**
	 * Checks if property count exceeds the tab threshold.
	 *
	 * @private
	 * @param {*} properties
	 * The properties object to check.
	 * @returns {boolean}
	 * True if property count exceeds threshold.
	 * @memberof JsonSchemaLayoutService
	 */
	private exceedsTabThreshold(properties: any): boolean
	{
		const propertyCount: number = Object.keys(properties).length;

		return propertyCount > this.tabThreshold;
	}

	/**
	 * Determines if an object property should use tab organization.
	 * Tabs are created when property count exceeds threshold (>9 fields)
	 * or when the object contains nested complexity (arrays or nested objects).
	 *
	 * @private
	 * @param {*} property
	 * The property schema definition.
	 * @returns {boolean}
	 * True if tabs should be used.
	 * @memberof JsonSchemaLayoutService
	 */
	private shouldUseTab(property: any): boolean
	{
		const properties: any = this.getSchemaProperties(property);

		if (AnyHelper.isNull(properties))
		{
			return false;
		}

		if (this.exceedsTabThreshold(properties))
		{
			return true;
		}

		return this.hasNestedComplexity(properties);
	}

	/**
	 * Determines if an object property contains nested complexity
	 * (arrays or nested objects with properties).
	 *
	 * @private
	 * @param {*} properties
	 * The properties object to check.
	 * @returns {boolean}
	 * True if nested arrays or nested objects exist.
	 * @memberof JsonSchemaLayoutService
	 */
	private hasNestedComplexity(properties: any): boolean
	{
		for (const propName of Object.keys(properties))
		{
			const prop: any = properties[propName];
			const propType: string = this.getPropertyType(prop ?? {});

			if (propType === this.schemaTypes.array)
			{
				return true;
			}

			if (propType === this.schemaTypes.object)
			{
				const nestedProps: any = this.getSchemaProperties(prop);

				if (!AnyHelper.isNull(nestedProps)
					&& Object.keys(nestedProps).length > 0)
				{
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Checks if a key represents a first-level data property.
	 *
	 * @private
	 * @param {string} key
	 * The property key path.
	 * @returns {boolean}
	 * True if key is first-level data property (e.g., 'data.propertyName').
	 * @memberof JsonSchemaLayoutService
	 */
	private isFirstLevelDataProperty(key: string): boolean
	{
		const keyParts: string[] =
			key.split(AppConstants.characters.period);
		const dataPrefix: string =
			this.dataPropertyPrefix.replace(
				AppConstants.characters.period,
				AppConstants.empty);

		return keyParts.length === 2 && keyParts[0] === dataPrefix;
	}

	/**
	 * Determines if anyOf repeaters should be wrapped in their own tab.
	 * Only first-level properties (data.*) with anyOf should be tabified.
	 *
	 * @private
	 * @param {string} key
	 * The field key path (e.g., 'data.interests').
	 * @returns {boolean}
	 * True if anyOf repeaters should be tabified.
	 * @memberof JsonSchemaLayoutService
	 */
	private shouldTabifyAnyOfRepeaters(key: string): boolean
	{
		return this.isFirstLevelDataProperty(key);
	}

	/**
	 * Wraps an array of anyOf repeaters in a tab with a section title.
	 *
	 * @private
	 * @param {string} propertyName
	 * The property name for the tab label.
	 * @param {FormlyFieldConfig[]} anyOfRepeaters
	 * Array of anyOf repeater configurations to wrap.
	 * @returns {FormlyFieldConfig}
	 * Tab configuration containing the repeaters.
	 * @memberof JsonSchemaLayoutService
	 */
	private wrapAnyOfRepeatersInTab(
		propertyName: string,
		anyOfRepeaters: FormlyFieldConfig[]): FormlyFieldConfig
	{
		const tabLabel: string =
			JsonSchemaLayoutHelper.generateLabel(propertyName);

		return JsonSchemaLayoutHelper.wrapFieldsInTab(
			tabLabel,
			anyOfRepeaters);
	}
}